// ===== Interfaces =====

import {
    ICoord,
    IInlineLinkCardPlacement,
} from '../interfaces';

interface IResolveInlineLinkCardPlacementArgs {
    linkRect: {
        top: number,
        bottom: number,
        left: number,
        width: number,
    },
    anchorPoint?: ICoord,
    cardWidth: number,
    cardHeight: number,
    viewportWidth: number,
    viewportHeight: number,
    viewportPadding: number,
    verticalOffset: number,
}

const clamp = (
    value: number,
    min: number,
    max: number,
): number => Math.min(max, Math.max(min, value));

/**
 * Resolves a stable viewport-relative placement for the inline-link hover card.
 * The card keeps its vertical position tied to the link while allowing the
 * horizontal anchor to follow the pointer position that opened the card.
 */
const resolveInlineLinkCardPlacement = ({
    linkRect,
    anchorPoint,
    cardWidth,
    cardHeight,
    viewportWidth,
    viewportHeight,
    viewportPadding,
    verticalOffset,
}: IResolveInlineLinkCardPlacementArgs): IInlineLinkCardPlacement => {
    const safeCardWidth = Math.max(0, cardWidth);
    const safeCardHeight = Math.max(0, cardHeight);
    const minLeft = viewportPadding;
    const maxLeft = Math.max(
        viewportPadding,
        viewportWidth - safeCardWidth - viewportPadding,
    );
    const anchorX = anchorPoint?.x ?? linkRect.left + (0.5 * linkRect.width);
    const centeredLeft = anchorX - (0.5 * safeCardWidth);
    const left = clamp(centeredLeft, minLeft, maxLeft);

    const aboveTop = linkRect.top - safeCardHeight - verticalOffset;
    const belowTop = linkRect.bottom + verticalOffset;
    const minTop = viewportPadding;
    const maxTop = Math.max(
        viewportPadding,
        viewportHeight - safeCardHeight - viewportPadding,
    );
    const cardAbove = aboveTop >= viewportPadding || belowTop > maxTop;

    return {
        left,
        top: clamp(cardAbove ? aboveTop : belowTop, minTop, maxTop),
        cardAbove,
    };
};

export default resolveInlineLinkCardPlacement;
